/**
 * muiAlerts — MUI-based drop-in replacement for SweetAlert2.
 *
 * Provides a `MuiAlert.fire()` API that mirrors Swal.fire(), rendering
 * a Material UI Dialog instead of the SweetAlert2 overlay. Supports:
 *   - Simple call: MuiAlert.fire('Title', 'Message', 'success'|'error'|'warning'|'info')
 *   - Object call: MuiAlert.fire({ title, text, icon, showCancelButton, confirmButtonText,
 *                                   cancelButtonText, preConfirm })
 *   - preConfirm: async function that can throw to display a validation message
 *   - MuiAlert.showValidationMessage(msg): programmatically set validation error
 *   - MuiAlert.isLoading(): check if a preConfirm is in progress
 *
 * Theme is read at mount time from `data-theme-mode` on <html> so the dialog
 * respects the app's current dark/light mode.
 */
import React, { useMemo, useState } from 'react'
import { createRoot } from 'react-dom/client'
import {
    Alert,
    Button,
    CircularProgress,
    CssBaseline,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    ThemeProvider,
    Typography,
    createTheme,
} from '@mui/material'

// Module-level ref so showValidationMessage can reach the currently active dialog
let activeController = null

// Maps SweetAlert2 icon names to MUI Alert severity values
const severityFromIcon = (icon) => {
    if (icon === 'success' || icon === 'error' || icon === 'warning' || icon === 'info') {
        return icon
    }
    if (icon === 'danger') {
        return 'error'
    }
    return 'info'
}

// Normalises the overloaded fire() signature into a single options object
const parseOptions = (arg1, arg2, arg3) => {
    if (typeof arg1 === 'object' && arg1 !== null) {
        return {
            title: arg1.title || 'Notice',
            text: arg1.text || arg1.html || '',
            icon: arg1.icon || 'info',
            showCancelButton: Boolean(arg1.showCancelButton),
            confirmButtonText: arg1.confirmButtonText || 'OK',
            cancelButtonText: arg1.cancelButtonText || 'Cancel',
            preConfirm: arg1.preConfirm,
        }
    }

    return {
        title: arg1 || 'Notice',
        text: arg2 || '',
        icon: arg3 || 'info',
        showCancelButton: false,
        confirmButtonText: 'OK',
        cancelButtonText: 'Cancel',
        preConfirm: null,
    }
}

// Reads the current theme mode from the HTML attribute set by the MUI ThemeProvider wrapper
const modeFromDom = () => (document?.documentElement?.getAttribute('data-theme-mode') === 'dark' ? 'dark' : 'light')

const MuiAlertDialog = ({ options, onResolve, onClose }) => {
    const [loading, setLoading] = useState(false)
    const [validationMessage, setValidationMessage] = useState('')

    const severity = severityFromIcon(options.icon)
    const theme = useMemo(() => createTheme({ palette: { mode: modeFromDom() } }), [])

    activeController = {
        setValidationMessage,
        isLoading: () => loading,
    }

    const handleCancel = () => {
        onResolve({ isConfirmed: false, isDismissed: true })
        onClose()
    }

    const handleConfirm = async () => {
        if (!options.preConfirm) {
            onResolve({ isConfirmed: true })
            onClose()
            return
        }

        try {
            setLoading(true)
            setValidationMessage('')
            const value = await options.preConfirm(true)
            setLoading(false)
            onResolve({ isConfirmed: true, value })
            onClose()
        } catch (error) {
            setLoading(false)
            setValidationMessage(error?.message || 'Something went wrong')
        }
    }

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Dialog open onClose={loading ? undefined : handleCancel} fullWidth maxWidth="xs">
                <DialogTitle>{options.title}</DialogTitle>
                <DialogContent>
                    {options.text ? (
                        <Alert severity={severity} sx={{ mt: 1 }}>
                            {options.text}
                        </Alert>
                    ) : null}
                    {validationMessage ? (
                        <Typography sx={{ mt: 1.5 }} color="error.main" variant="body2">
                            {validationMessage}
                        </Typography>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    {options.showCancelButton ? (
                        <Button onClick={handleCancel} disabled={loading}>
                            {options.cancelButtonText}
                        </Button>
                    ) : null}
                    <Button onClick={handleConfirm} variant="contained" disabled={loading}>
                        {loading ? <CircularProgress size={16} color="inherit" /> : options.confirmButtonText}
                    </Button>
                </DialogActions>
            </Dialog>
        </ThemeProvider>
    )
}

/**
 * Mounts a MuiAlertDialog into a temporary DOM node and returns a Promise
 * that resolves with { isConfirmed, isDismissed, value? } when the user acts.
 * The temporary node is removed from the DOM after the dialog closes.
 */
const mountDialog = (options) => {
    return new Promise((resolve) => {
        const container = document.createElement('div')
        document.body.appendChild(container)
        const root = createRoot(container)

        const cleanup = () => {
            activeController = null
            root.unmount()
            container.remove()
        }

        root.render(
            <MuiAlertDialog
                options={options}
                onResolve={resolve}
                onClose={cleanup}
            />
        )
    })
}

const muiAlert = {
    fire: (...args) => {
        const options = parseOptions(...args)
        return mountDialog(options)
    },
    showValidationMessage: (message) => {
        if (activeController?.setValidationMessage) {
            activeController.setValidationMessage(message)
        }
    },
    isLoading: () => {
        if (activeController?.isLoading) {
            return activeController.isLoading()
        }
        return false
    }
}

export default muiAlert
